import { ReactNode } from 'react'

// Environment variables type definition
export interface ProcessEnv {
  NEXT_PUBLIC_API_BASE_URL: string
  JWT_SECRET: string
  NEXT_PUBLIC_APP_URL: string
  DATABASE_URL?: string
  NEXT_PUBLIC_GOOGLE_ANALYTICS_ID?: string
}

// User roles enum
/* eslint-disable @typescript-eslint/no-unused-vars, no-unused-vars */
export enum UserRole {
  ADMIN = 'admin',
  MANAGER = 'manager',
  AGENT = 'agent',
  USER = 'user',
}
/* eslint-enable @typescript-eslint/no-unused-vars, no-unused-vars */

// API Response types
export interface ApiResponse<T = unknown> {
  success: boolean
  data?: T
  error?: string
  message?: string
  code?: string
  statusCode?: number
  details?: Array<{
    code?: string
    field?: string
    details?: Record<string, unknown>
  }>
}

// User and Authentication types
export interface User {
  id: string
  email: string
  name: string
  avatar?: string
  role: UserRole
  createdAt: string
  updatedAt: string
}

export interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
}

export interface LoginCredentials {
  email: string
  password: string
}

export interface RegisterCredentials {
  token: string
  fullName: string
  password: string
}

export interface PasswordResetPayload {
  email: string
}

// Theme types
export type Theme = 'light' | 'dark'

export interface ThemeState {
  theme: Theme
  toggleTheme: () => void
  setTheme: (_theme: Theme) => void
}

// Example entity types for CRUD operations
export interface Post {
  id: string
  title: string
  content: string
  excerpt?: string
  author: User
  publishedAt: string
  updatedAt: string
  tags: string[]
  isPublished: boolean
}

export interface CreatePostData {
  title: string
  content: string
  excerpt?: string
  tags: string[]
  isPublished: boolean
}

export interface UpdatePostData extends Partial<CreatePostData> {
  id: string
}

// Role and User Management types
export interface Role {
  id: string
  name: string
}

export interface GetRolesResponse {
  roles: Role[]
  statusCode: number
  message: string[]
}

export interface InviteUserPayload {
  email: string
  fullName: string
  roleId: string
}

export interface InviteUserResponse {
  statusCode: number
  message: string[]
}

export interface InvitedUser {
  id: string
  email: string
  fullName: string
  roleId: string
  roleName: string
  token: string
  isValid: boolean
  expireDate: string
  status: number
  appliedDateTime: string | null
  createdAt: string
}

export interface Pagination {
  page: number
  pageSize: number
  totalCount: number
  totalPages: number
  hasPrevious: boolean
  hasNext: boolean
}

export interface GetInvitedUsersResponse {
  totalCount: number
  invitedUsers: InvitedUser[]
  pagination: Pagination
  statusCode: number
  message: string[]
}

export interface InvitationInfo {
  id: string
  email: string
  fullName: string
  roleId: string
  roleName: string
  token: string
  isValid: boolean
  expireDate: string
  status: number
  appliedDateTime: string | null
  createdAt: string
}

export interface GetInvitationInfoResponse {
  invitation: InvitationInfo
  statusCode: number
  message: string[]
}

export interface CompleteInvitationPayload {
  token: string
  fullName: string
  password: string
}

// Event types
export interface Event {
  eventId: string
  name: string
  artist?: string
  date: string
  role?: string
  status: 'Pending' | 'Active' | 'Cancelled' | 'Completed'
  progress?: number
  uploadedFileCount?: number
  createdAt?: string
}

export interface CreateEventPayload {
  userId: string
  name: string
  date: string
}

export interface CreateEventResponse {
  statusCode: number
  message: string[]
}

export interface GetEventsResponse {
  events: Event[]
  statusCode: number
  message: string[]
}

// Booking types
export interface BookingData {
  artistName: string
  artistProfileId: string
  room: string
  startTime: { hour: number; minute: number } | null
  endTime: { hour: number; minute: number } | null
  deadlineDate: string
  selectedAgents: Array<{ agentEmail: string; contactName: string | null }>
}

export interface CreateBookingPayload {
  userId: string
  eventId: string
  artistProfileId: string
  agentEmails: string[]
  deadline: string
  setStartTime: { hour: number; minute: number }
  setEndTime: { hour: number; minute: number }
  room: string
}

export interface CreateBookingResponse {
  statusCode: number
  message: string[]
}

export interface RelatedBooking {
  stageName: ReactNode
  bookingId: string
  artistName: string
  eventName: string
  date: string
  progress: number
  status: string
}

// Booking List Item (for bookings list page)
export interface BookingListItem {
  stageName: ReactNode
  bookingId: string
  bookingIndex: string
  artistName: string
  eventId: string
  eventName: string
  date: string
  progress: number
  status:
    | 'Created'
    | 'Send to Agent'
    | 'Agent completed'
    | 'Sent to Finalize'
    | 'Completed'
  bookingStatus?: string
  bookingStatusDate?: string
}

// Get Manager/Agent Bookings Response
export interface GetBookingsResponse {
  bookings: BookingListItem[]
  statusCode: number
  message: string[]
}

// Event Detail types
export interface EventDetail {
  eventId: string
  name: string
  date: string
  managerId: string
  managerName: string
  lastUpdate: string
  lastUpdateUserName: string
}

export interface GetEventDetailResponse {
  event: EventDetail
  relatedBookings: RelatedBooking[]
  technicalRiders?: TechnicalRider[]
  statusCode: number
  message: string[]
}

// Booking Detail types
export interface BookingDetail {
  bookingId: string
  // Main Event Info
  eventName: string
  eventDate: string
  // Agent Info
  agentName?: string
  agentEmail?: string
  // Logon Info
  logonName?: string
  logonDate?: string
  logonContact?: string
  // Documents
  documents?: any[]
  // Technical Requirements
  mixerType?: string
  templates?: string
  additionalTemplates?: string
  comment?: string
  // Flight Info
  flightInfo?: any[]
  // Hotel Info
  hotelCheckIn?: string
  hotelCheckOut?: string
  hotelName?: string
  // Guest List
  guestList?: string[]
  // Soundcheck
  soundcheckDate?: string
  soundcheckTime?: string
  // Agent Requests
  agentRequests?: AgentRequest[]
}

// Form objects with update tracking
export interface LegalInfo {
  id?: string
  stageName?: string
  artistLegalName?: string
  artistEmail?: string
  artistPhone?: string
  artistInstagramUrl?: string
  artistContactName?: string
  legalDocumentNumber?: string
  idExpirationDate?: string
  lastUpdate?: string
  lastUpdateUserName?: string
  documents?: Array<{
    documentId?: string
    id?: string
    documentName?: string
    fileName?: string
    name?: string
    documentSize?: string
    fileSize?: string
    documentLink?: string
    documentOpenLink?: string
    fileUrl?: string
    url?: string
  }>
}

export interface TechnicalRequirement {
  id?: string
  mixerType?: string
  turntables2x?: string
  additionalTurntable2x?: string
  comment?: string
  lastUpdate?: string
  lastUpdateUserName?: string
  documents?: Array<{
    documentId?: string
    id?: string
    documentName?: string
    fileName?: string
    name?: string
    documentSize?: string
    fileSize?: string
    documentLink?: string
    documentOpenLink?: string
    fileUrl?: string
    url?: string
  }>
}

// For backwards compatibility
export type TechnicalRequirements = TechnicalRequirement

export interface TechnicalRider {
  id?: string
  artistName?: string
  mixerType?: string
  turntables2x?: string
  additionalTurntable2x?: string
  comment?: string
  lastUpdate?: string
  lastUpdateUserName?: string
  documents?: Array<{
    documentId?: string
    id?: string
    documentName?: string
    fileName?: string
    name?: string
    documentSize?: string
    fileSize?: string
    documentLink?: string
    documentOpenLink?: string
    fileUrl?: string
    url?: string
  }>
}

export interface HotelInfo {
  id?: string
  checkInDate?: string
  checkOutDate?: string
  hotelName?: string
  earlyCheckIn?: boolean
  lateCheckOut?: boolean
  lastUpdate?: string
  lastUpdateUserName?: string
}

export interface FlightInfo {
  arrivalDateTime?: string
  departureDateTime?: string
  arrivalFlightNumber?: string
  departureFlightNumber?: string
  lastUpdate?: string
  lastUpdateUserName?: string
}

export interface Flight {
  id: string
  flightNumber: string
  flightDate: string
  flightType: 1 | 2 // 1 = arrival, 2 = departure
  flightStatus: string
  lastUpdate: string
  lastUpdateUserName: string
  // Extended flight information from new API response
  queryFlight?: string
  flightIata?: string
  rawStatus?: string
  departureDelayMinutes?: number
  arrivalDelayMinutes?: number
  departureIata?: string
  departureAirport?: string
  arrivalIata?: string
  arrivalAirport?: string
  gate?: string
  terminal?: string
  movementTime?: string
  flightDateWithTime?: string // Format: "2025 December 19 13:37"
}

export interface FlightDocument {
  id: string
  documentName: string
  documentSize: string
  documentLink: string
  documentOpenLink?: string
}

export interface GuestList {
  id?: string
  guestList?: string
  lastUpdate?: string
  lastUpdateUserName?: string
}

export interface SoundCheck {
  id?: string
  soundcheckDate?: string
  soundcheckTime?: string
  lastUpdate?: string
  lastUpdateUserName?: string
}

export interface AgentRequest {
  agentId: string
  bookingId: string
  requestType: number
  requestTitle: string
  agentEmail: string
  agentFullName: string
  status: 'pending' | 'completed'
}

export interface GetManagerBookingResponse {
  booking: BookingDetail
  statusCode: number
  message: string[]
}

// Artist types
export interface ArtistListItem {
  id: string
  stageName: string
  legalName?: string
  email?: string
  phone?: string
  imageUrl?: string
  status?: number
  agentCount?: number
  agents?: Array<{
    id: string
    name: string
    email: string
  }>
  createdAt?: string
  lastUpdated?: string
}

export interface ArtistProfile {
  id: string
  // Basic Info
  stageName: string
  legalName?: string
  email?: string
  phone?: string
  instagramUrl?: string
  idExpirationDate?: string | null
  imageUrl?: string
  status?: number
  agents?: Array<{
    id: string
    agentId: string
    agentEmail: string
    contactName: string
    fullName: string
    status: number
  }>
  // Technical Requirements
  technicalRequirements?: TechnicalRequirement[]
  // Rider Notes
  riderNotes?: {
    value?: string
    lastUpdate?: string
    lastUpdateUserName?: string
  }
  // Food & Beverages Restrictions
  foodRestrictions?: {
    value?: string
    lastUpdate?: string
    lastUpdateUserName?: string
  }
  // Documents
  documents?: Array<{
    id?: string
    createdAt?: string
    documentName?: string
    documentSize?: string
    documentLink?: string
    documentOpenLink?: string
  }>
  // Metadata
  createdAt: string
  lastUpdate?: string
  lastUpdateUserName?: string
}

export interface GetArtistsResponse {
  artists: ArtistListItem[]
  statusCode: number
  message: string[]
}

export interface GetArtistProfileResponse {
  artist: ArtistProfile
  statusCode: number
  message: string[]
}

export interface CreateArtistPayload {
  userId: string
  stageName: string
  legalName?: string
  email?: string
  phone?: string
  agentEmails?: string[]
}

export interface UpdateArtistPayload {
  artistId: string
  stageName?: string
  legalName?: string
  email?: string
  phone?: string
  agentEmails?: string[]
  instagram?: string
  ipExpirationDate?: string
}

// Pagination types
export interface PaginationInfo {
  page: number
  pageSize: number
  totalCount: number
  totalPages: number
  hasPrevious: boolean
  hasNext: boolean
}

// Agent types
export interface AgentMinimal {
  agentId: string
  email: string
  fullName: string
}

// Artist Document State for file upload handling
export interface ArtistDocumentState {
  newFiles: File[]
  newFileDocs: { id: string; name: string; size: string }[]
  existingDocs: { id: string; name: string; size: string; url?: string }[]
}

// SEO Meta types
export interface SeoMeta {
  title: string
  description: string
  keywords?: string
  ogTitle?: string
  ogDescription?: string
  ogImage?: string
  ogUrl?: string
  twitterCard?: 'summary' | 'summary_large_image'
  twitterTitle?: string
  twitterDescription?: string
  twitterImage?: string
}
